"use client";
import { useCallback, useEffect, useRef } from "react"
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { getEditorExtension } from "@/tiptap/lib/tiptap-advanced-utils"
import { selectNodeAndHideFloating } from "@/tiptap/hooks/use-floating-toolbar-visibility"
import { scrollHeadingIntoTocView } from "@/tiptap/nodes/toc-node/lib/toc-viewport"

/**
 * Hook to scroll to a specific hash in the Tiptap editor.
 * It finds the node with the given ID and scrolls it into view.
 *
 * @param config Configuration for the scroll behavior
 * @returns Function to scroll to a specific hash
 */
export function useScrollToHash(config = {}) {
  const {
    editor: providedEditor,
    onTargetFound = () => {},
    onTargetNotFound = () => {},
    topOffset = 0,
  } = config

  const { editor } = useTiptapEditor(providedEditor)

  const lastProcessedHashRef = useRef(null)

  const onTargetFoundRef = useRef(onTargetFound)
  const onTargetNotFoundRef = useRef(onTargetNotFound)
  onTargetFoundRef.current = onTargetFound
  onTargetNotFoundRef.current = onTargetNotFound

  const scrollToNode = useCallback(id => {
    if (!editor) return false

    const attributeName =
      getEditorExtension(editor, "uniqueID")?.options?.attributeName ??
      "data-id"
    let position = null

    editor.state.doc.descendants((node, pos) => {
      if (node.attrs?.[attributeName] === id) {
        position = pos
        return false
      }
      return true
    })

    if (position === null) return false

    setTimeout(() => {
      const dom = editor.view.nodeDOM(position)

      if (dom) {
        scrollHeadingIntoTocView(dom, { topOffset, behavior: "smooth" })
      }

      selectNodeAndHideFloating(editor, position)
    }, 0)

    return true
  }, [editor, topOffset])

  const handleScroll = useCallback((delay = 0) => {
    const hash = window.location.hash?.substring(1)
    if (!hash) return

    // Skip if we already scrolled to this exact hash
    if (hash === lastProcessedHashRef.current) return

    setTimeout(() => {
      if (scrollToNode(hash)) {
        lastProcessedHashRef.current = hash
        onTargetFoundRef.current(hash)
      } else {
        onTargetNotFoundRef.current(hash)
      }
    }, delay)
  }, [scrollToNode])

  useEffect(() => {
    if (!editor) return

    // Handle collaboration sync or immediate scroll
    const provider = editor.extensionManager.extensions.find((ext) => ext.name === "collaborationCaret")?.options?.provider

    if (provider?.on) {
      const syncHandler = () => handleScroll(500)
      provider.on("synced", syncHandler)

      return () => {
        provider.off?.("synced", syncHandler)
      };
    } else {
      handleScroll(500)
    }
  }, [editor, handleScroll])

  useEffect(() => {
    const immediateScroll = () => handleScroll()
    const delayedScroll = () => handleScroll(500)

    window.addEventListener("hashchange", immediateScroll)
    window.addEventListener("pageshow", delayedScroll)
    window.addEventListener("popstate", immediateScroll)

    return () => {
      window.removeEventListener("hashchange", immediateScroll)
      window.removeEventListener("pageshow", delayedScroll)
      window.removeEventListener("popstate", immediateScroll)
    };
  }, [handleScroll])

  return { scrollToHash: scrollToNode }
}
